import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, CreditCard, Calendar, Gift, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

// Helper to get API URL - uses current origin in production
const getApiUrl = () => {
  if (typeof window !== 'undefined' && window.location.origin.includes('xperience-guides.com')) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || window.location.origin;
};
const API_URL = getApiUrl();

// Subscription Card Component for Dashboard
export const SubscriptionCard = ({ user, onSubscribe }) => {
  const { i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const token = localStorage.getItem('vgt_token');
      const response = await fetch(`${API_URL}/api/subscription/create-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  const isActive = status?.subscription_active;
  const expiresAt = status?.subscription_expires_at ? new Date(status.subscription_expires_at) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border overflow-hidden"
      data-testid="subscription-card"
    >
      <div className={`p-4 ${isActive ? 'bg-green-50' : 'bg-purple-50'}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            isActive ? 'bg-green-100' : 'bg-purple-100'
          }`}>
            {isActive ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <Gift className="w-5 h-5 text-purple-600" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">
              {isActive 
                ? (i18n.language === 'fr' ? '🎉 Vous êtes membre !' : '🎉 You are a member!')
                : (i18n.language === 'fr' ? '✨ Rejoignez la communauté' : '✨ Join the community')
              }
            </h3>
            <p className="text-sm text-gray-600">
              {isActive 
                ? (i18n.language === 'fr' ? 'Accès illimité aux rencontres gratuites' : 'Unlimited access to free meetups')
                : (i18n.language === 'fr' ? 'Découvrez des expériences uniques et gratuites' : 'Discover unique free experiences')
              }
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {isActive ? (
          <>
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg p-3">
              <CheckCircle className="w-4 h-4" />
              <span>
                {i18n.language === 'fr' 
                  ? 'Vous avez accès à toutes les expériences gratuites'
                  : 'You have access to all free experiences'}
              </span>
            </div>
            
            {expiresAt && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar className="w-4 h-4" />
                <span>
                  {i18n.language === 'fr' ? 'Expire le' : 'Expires on'}{' '}
                  {expiresAt.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}
                </span>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-orange-800 mb-2">
                {i18n.language === 'fr' ? '🎁 Qu\'est-ce que c\'est ?' : '🎁 What is this?'}
              </h4>
              <p className="text-sm text-orange-700">
                {i18n.language === 'fr' 
                  ? 'Sur XPerience, nos guides proposent deux types d\'expériences : des expériences payantes (accessibles à tous) et des rencontres gratuites réservées aux membres.'
                  : 'On XPerience, our guides offer two types of experiences: paid experiences (accessible to everyone) and free meetups reserved for members.'}
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">
                {i18n.language === 'fr' ? 'Avec l\'abonnement, vous débloquez :' : 'With the subscription, you unlock:'}
              </h4>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{i18n.language === 'fr' 
                    ? 'Accès illimité aux rencontres gratuites (ateliers, balades, échanges culturels...)' 
                    : 'Unlimited access to free meetups (workshops, walks, cultural exchanges...)'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{i18n.language === 'fr' 
                    ? 'Rencontrez des passionnés locaux qui partagent bénévolement' 
                    : 'Meet local enthusiasts who share voluntarily'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{i18n.language === 'fr' 
                    ? 'Soutenez notre communauté de guides indépendants' 
                    : 'Support our community of independent guides'}
                  </span>
                </li>
              </ul>
            </div>

            <div className="text-xs text-gray-500 mt-2">
              {i18n.language === 'fr' 
                ? '💡 Les expériences payantes restent accessibles sans abonnement.'
                : '💡 Paid experiences remain accessible without subscription.'}
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <div className="text-3xl font-bold text-purple-700">49€</div>
              <div className="text-sm text-purple-600">{i18n.language === 'fr' ? 'par an' : 'per year'}</div>
              <div className="text-xs text-purple-500 mt-1">{i18n.language === 'fr' ? 'soit ~4€/mois' : 'only ~€4/month'}</div>
            </div>
            
            <Button 
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={handleSubscribe}
              disabled={subscribing}
              data-testid="subscribe-btn"
            >
              {subscribing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4 mr-2" />
              )}
              {i18n.language === 'fr' ? 'Souscrire maintenant' : 'Subscribe now'}
            </Button>
          </>
        )}
      </div>
    </motion.div>
  );
};

// Success Page
export const SubscriptionSuccessPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const { refreshUser } = useAuth();

  useEffect(() => {
    verifySubscription();
  }, []);

  const verifySubscription = async () => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      setVerifying(false);
      return;
    }

    // Poll for payment status
    const maxAttempts = 5;
    let attempts = 0;

    const pollStatus = async () => {
      try {
        const token = localStorage.getItem('vgt_token');
        const response = await fetch(`${API_URL}/api/subscription/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ session_id: sessionId })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setSuccess(true);
          setVerifying(false);
          refreshUser();
          toast.success(i18n.language === 'fr' ? 'Abonnement activé !' : 'Subscription activated!');
          return;
        }
        
        // If not yet paid, continue polling
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollStatus, 2000);
        } else {
          setVerifying(false);
        }
      } catch (error) {
        console.error('Error verifying subscription:', error);
        setVerifying(false);
      }
    };

    pollStatus();
  };

  return (
    <main className="min-h-screen pt-20 pb-16 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full mx-4 bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        {verifying ? (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-purple-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">
              {i18n.language === 'fr' ? 'Vérification...' : 'Verifying...'}
            </h1>
          </>
        ) : success ? (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {i18n.language === 'fr' ? 'Abonnement activé !' : 'Subscription activated!'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {i18n.language === 'fr' 
                ? 'Vous pouvez maintenant proposer des expériences gratuites.'
                : 'You can now offer free experiences.'}
            </p>
            <Button onClick={() => navigate('/dashboard')}>
              {i18n.language === 'fr' ? 'Aller au tableau de bord' : 'Go to dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        ) : (
          <>
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold mb-2">
              {i18n.language === 'fr' ? 'Erreur' : 'Error'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {i18n.language === 'fr' 
                ? 'Une erreur est survenue lors de la vérification.'
                : 'An error occurred during verification.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              {i18n.language === 'fr' ? 'Retour' : 'Go back'}
            </Button>
          </>
        )}
      </motion.div>
    </main>
  );
};

// Cancel Page
export const SubscriptionCancelPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  return (
    <main className="min-h-screen pt-20 pb-16 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full mx-4 bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-gray-400" />
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {i18n.language === 'fr' ? 'Paiement annulé' : 'Payment cancelled'}
        </h1>
        <p className="text-muted-foreground mb-6">
          {i18n.language === 'fr' 
            ? 'Vous pouvez réessayer quand vous voulez.'
            : 'You can try again whenever you want.'}
        </p>
        <Button variant="outline" onClick={() => navigate('/dashboard')}>
          {i18n.language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
        </Button>
      </motion.div>
    </main>
  );
};

export default SubscriptionCard;
